import { useEffect, useMemo, useCallback } from 'react'
import PropTypes from 'prop-types'

import { ASC, MENU_ID } from './constants'
import { getColumnActions, getValue } from './utils'
import {
  Table as TableComponent,
  Head,
  Header,
  Body,
  Row,
  DisabledRow,
  Cell,
  MenuCell,
  DataCell,
  SortedCell,
  SortableHeader,
  SortedHeader,
  SortIcon,
  CheckboxWrapper
} from './components'
import { Pagination } from './pagination'
import { Highlighter } from './highlighter'

import { Checkbox } from '../../inputs/checkbox'

const Table = ({
  data,
  query,
  columns,
  sortBy,
  sortOrder,
  onSort,
  rowIdKey,
  onClickRow,
  onSelectRow,
  isRowDisabled,
  isSelectable,
  isSearchQueryHighlighted,
  selectedRows,
  setSelectedRows
}) => {
  const { renderers } = useMemo(() => getColumnActions(columns), [columns])
  const randomValues = useMemo(
    () => window.crypto.getRandomValues(new Uint32Array(Math.max(...Pagination.lengths))),
    []
  )
  const getRowId = useMemo(
    () => (rowIdKey ? row => row[rowIdKey] : (row, rowIdx) => randomValues[rowIdx]),
    [rowIdKey, randomValues]
  )
  const handleRowClick = (evt, row) => {
    if (!window.getSelection().toString()) {
      onClickRow(evt, row)
    }
  }
  const getCurrentPageSelections = () => {
    return data.filter(row =>
      selectedRows.some(selection => getRowId(selection) === getRowId(row))
    )
  }
  const enabledRows = useMemo(() => {
    return data.filter(row => !isRowDisabled(row))
  }, [data, isRowDisabled])
  const handleRowSelect = useCallback(
    id => {
      setSelectedRows(selections => {
        const row = data.find(item => getRowId(item) === id)
        const rowId = getRowId(row)
        let next = null

        if (!selections.some(selection => getRowId(selection) === rowId)) {
          next = [...selections, row]
        } else {
          next = selections.filter(selection => getRowId(selection) !== rowId)
        }

        return next
      })
    },
    [setSelectedRows, getRowId, data]
  )
  const handleRowSelectAll = useCallback(() => {
    setSelectedRows(selections => {
      const currentPageSelections = data.filter(row =>
        selections.some(selection => getRowId(selection) === getRowId(row))
      )
      let next = null

      if (currentPageSelections.length === enabledRows.length) {
        next = selections.filter(
          selection =>
            !currentPageSelections.some(row => getRowId(row) === getRowId(selection))
        )
      } else {
        next = [
          ...selections,
          ...data.filter(
            row =>
              !isRowDisabled(row) &&
              selections.every(selection => getRowId(selection) !== getRowId(row))
          )
        ]
      }

      return next
    })
  }, [data, getRowId, setSelectedRows, isRowDisabled, enabledRows])

  useEffect(() => {
    onSelectRow(selectedRows)
  }, [selectedRows, onSelectRow])

  return (
    <TableComponent>
      <Head>
        <Row>
          {isSelectable && (
            <Header size="50px">
              <CheckboxWrapper>
                <Checkbox
                  isChecked={getCurrentPageSelections().length > 0}
                  isDisabled={enabledRows.length === 0}
                  checkedStyle={
                    getCurrentPageSelections().length !== enabledRows.length
                      ? 'line'
                      : 'default'
                  }
                  onChange={handleRowSelectAll}
                />
              </CheckboxWrapper>
            </Header>
          )}
          {columns.map(({ id, label, labelAlign, sortable, sort, width }) => {
            const size = width || 'auto'

            if (sortable !== false || sort) {
              const isActive = id === sortBy
              const direction = isActive ? sortOrder : ASC
              const SortHeader = isActive ? SortedHeader : SortableHeader

              return (
                <SortHeader
                  role="button"
                  tabIndex={0}
                  key={id}
                  data-testid={`header-${id}`}
                  size={size}
                  onClick={() => onSort(id)}
                  onKeyUp={evt => {
                    if (evt.key === 'Enter') {
                      onSort(id)
                    }
                  }}
                  labelAlign={labelAlign}>
                  {label}
                  <SortIcon
                    direction={direction}
                    aria-sort={
                      isActive ? (direction === ASC ? 'ascending' : 'descending') : 'none'
                    }
                  />
                </SortHeader>
              )
            }

            return (
              <Header
                key={id}
                data-testid={`header-${id}`}
                size={size}
                labelAlign={labelAlign}>
                {label}
              </Header>
            )
          })}
        </Row>
      </Head>
      <Body>
        {data.map((row, idx) => {
          const isDisabled = isRowDisabled(row)
          const TableRow = isDisabled ? DisabledRow : Row

          return (
            <TableRow
              key={getRowId(row, idx)}
              onClick={onClickRow ? evt => handleRowClick(evt, row) : null}>
              {isSelectable && (
                <Cell onClick={evt => evt.stopPropagation()}>
                  <CheckboxWrapper>
                    <Checkbox
                      isChecked={selectedRows.some(
                        selection => getRowId(selection) === getRowId(row)
                      )}
                      isDisabled={isDisabled}
                      onChangeArg={getRowId(row)}
                      onChange={handleRowSelect}
                    />
                  </CheckboxWrapper>
                </Cell>
              )}
              {columns.map(({ id, label }) => {
                if (label) {
                  const TableCell = id === sortBy ? SortedCell : DataCell
                  let title = getValue(row, id)

                  if (typeof title !== 'string') {
                    title = ''
                  }

                  return (
                    <TableCell key={`${id}-${idx}`} title={title}>
                      {isSearchQueryHighlighted && query ? (
                        <Highlighter mark={query} text={renderers[id](row)} />
                      ) : (
                        renderers[id](row)
                      )}
                    </TableCell>
                  )
                }

                if (id === MENU_ID) {
                  return (
                    <MenuCell key={`${id}-${idx}`} onClick={evt => evt.stopPropagation()}>
                      {renderers[id](row)}
                    </MenuCell>
                  )
                }

                return <Cell key={`${id}-${idx}`}>{renderers[id](row)}</Cell>
              })}
            </TableRow>
          )
        })}
      </Body>
    </TableComponent>
  )
}

Table.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      labelAlign: PropTypes.oneOf(['left', 'center', 'right']),
      sortable: PropTypes.bool,
      render: PropTypes.func,
      sort: PropTypes.func,
      width: PropTypes.string,
      searchKeys: PropTypes.array
    })
  ).isRequired,
  rowIdKey: PropTypes.string,
  isRowDisabled: PropTypes.func.isRequired,
  onClickRow: PropTypes.func,
  onSelectRow: PropTypes.func,
  onSort: PropTypes.func.isRequired,
  sortBy: PropTypes.string.isRequired,
  sortOrder: PropTypes.string.isRequired,
  isSelectable: PropTypes.bool,
  isSearchQueryHighlighted: PropTypes.bool,
  query: PropTypes.string,
  selectedRows: PropTypes.array,
  setSelectedRows: PropTypes.func
}

export { Table }
